import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { UploadCloud, FileSpreadsheet, Loader2, ReceiptText, FileCheck2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { MappingDialog } from "@/components/MappingDialog";

const isTabular = (name) => /\.(csv|xlsx|xls)$/i.test(name || "");

export const UploadZone = ({ period, onUploaded }) => {
  const [mode, setMode] = useState("statement"); // statement | check
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [mapOpen, setMapOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);

  const onDrop = useCallback(
    async (files) => {
      if (!files.length) return;
      setBusy(true);
      setProgress(0);
      try {
        if (mode === "check") {
          let added = 0;
          for (const file of files) {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("period", period || "");
            const res = await api.uploadCheck(fd);
            added += 1;
            if (res.duplicates?.length) {
              toast.warning(
                `Possible duplicate: "${res.transaction.description}" ($${res.transaction.amount}) matches ${res.duplicates.length} existing transaction(s). Review in the table.`,
                { duration: 7000 }
              );
            }
          }
          toast.success(`${added} check(s) added as expenses`);
          onUploaded?.();
        } else if (files.length > 1) {
          // Bulk: drop all statements at once — auto-detect columns, no per-file dialog
          let ok = 0, total = 0, failed = 0;
          for (const file of files) {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("period", period || "");
            try {
              const res = await api.upload(fd);
              ok += 1;
              total += res.inserted || 0;
            } catch (e) {
              failed += 1;
            }
          }
          toast.success(`${total} transactions imported from ${ok} file(s)${failed ? ` — ${failed} failed` : ""}`);
          onUploaded?.();
        } else {
          const file = files[0];
          if (isTabular(file.name)) {
            const fd = new FormData();
            fd.append("file", file);
            const pv = await api.previewUpload(fd);
            setPreview(pv);
            setPendingFile(file);
            setMapOpen(true);
          } else {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("period", period || "");
            const res = await api.upload(fd, (e) => {
              if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
            });
            toast.success(`${res.inserted} transactions imported & categorized`);
            onUploaded?.();
          }
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed");
      } finally {
        setBusy(false);
        setProgress(0);
      }
    },
    [mode, period, onUploaded]
  );

  const onDropRejected = useCallback((rejections) => {
    const names = rejections.map((r) => r.file.name).join(", ");
    toast.error(`Couldn't accept: ${names}. Try a .csv, .xlsx, .xls, or PDF file.`);
  }, []);

  const validator = (file) => {
    const ok =
      mode === "check"
        ? /\.(png|jpe?g|webp|tiff?|bmp|pdf)$/i.test(file.name)
        : /\.(csv|xlsx|xls|pdf)$/i.test(file.name);
    return ok ? null : { code: "unsupported", message: "Unsupported file type" };
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    validator,
    multiple: true,
    disabled: busy,
  });

  const Toggle = ({ value, icon: Icon, label }) => (
    <button
      type="button"
      data-testid={`upload-mode-${value}`}
      onClick={(e) => {
        e.stopPropagation();
        setMode(value);
      }}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
        mode === value ? "bg-black text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
      }`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <>
      <div className="flex items-center gap-2 mb-3">
        <Toggle value="statement" icon={ReceiptText} label="Bank Statement" />
        <Toggle value="check" icon={FileCheck2} label="Check Copy" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        {...getRootProps()}
        data-testid="upload-dropzone"
        className={`border-2 border-dashed p-10 md:p-14 flex flex-col items-center justify-center rounded-lg text-center cursor-pointer transition-all duration-200 ${
          isDragActive ? "border-black bg-gray-50" : "border-gray-300 hover:border-black hover:bg-gray-50"
        }`}
      >
        <input {...getInputProps()} data-testid="upload-input" />
        {busy ? (
          <>
            <Loader2 className="h-10 w-10 text-black animate-spin mb-4" />
            <p className="font-heading font-bold text-gray-900">Processing…</p>
            <p className="text-sm text-gray-500 mt-1">
              {progress > 0 ? `${progress}% uploaded` : "Reading your file"}
            </p>
          </>
        ) : mode === "check" ? (
          <>
            <div className="h-14 w-14 rounded-full bg-black flex items-center justify-center mb-4">
              <FileCheck2 className="h-7 w-7 text-white" />
            </div>
            <p className="font-heading text-lg font-extrabold text-gray-900">Drop check copies here</p>
            <p className="text-sm text-gray-500 mt-1 max-w-md">
              Upload photos/scans (PNG, JPG) or PDFs of physical checks. We read the payee, amount, date
              and check number, then add each as an expense to <b>{period || "the current month"}</b>.
            </p>
            <div className="flex items-center gap-2 mt-4 text-xs uppercase tracking-[0.15em] font-semibold text-gray-500">
              <FileCheck2 className="h-4 w-4" /> PNG · JPG · PDF · multiple
            </div>
          </>
        ) : (
          <>
            <div className="h-14 w-14 rounded-full bg-black flex items-center justify-center mb-4">
              <UploadCloud className="h-7 w-7 text-white" />
            </div>
            <p className="font-heading text-lg font-extrabold text-gray-900">
              Drop your bank statements here
            </p>
            <p className="text-sm text-gray-500 mt-1 max-w-md">
              Drop <b>one or many</b> statements at once — Excel (.xlsx/.xls), CSV, or PDF. Everything
              is added to <b>{period || "the current month"}</b> and categorized for your P&L.
            </p>
            <div className="flex items-center gap-2 mt-4 text-xs uppercase tracking-[0.15em] font-semibold text-gray-500">
              <FileSpreadsheet className="h-4 w-4" /> XLSX · CSV · PDF · multiple · OCR
            </div>
          </>
        )}
      </motion.div>

      <MappingDialog
        open={mapOpen}
        onOpenChange={setMapOpen}
        file={pendingFile}
        preview={preview}
        period={period}
        onImported={onUploaded}
      />
    </>
  );
};
